import { cva, cx, VariantProps } from "class-variance-authority";

import {
    MAX_VISIBLE_PAGES,
    PAGE_SIZE_OPTIONS,
    FIXED_WIDTH_PAGE_COUNT,
} from "./consts";
import { PageSizeSelect } from "./PageSizeSelect";
import * as theme from "./Pagination.css";

import {
    PaginationNav,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "./";

const paginationStyle = cva("", {
    variants: {
        variant: {
            listView: theme.paginationListView,
            inline: "",
        },
    },
    defaultVariants: {
        variant: "listView",
    },
});

interface PaginationProps {
    current: number;
    onChangePage: (page: number) => void;
    pageSize: number;
    total?: number;
    onPageSizeChange: (pageSize: number) => void;
    pageSizeOptions?: number[];
    getRowCountLabel:
        | ((pageSize: number, totalRowCount: number) => string)
        | null;
    getSinglePageRowCountLabel?: (totalRowCount: number) => string;
    pageLabel: string;
    totalRowCount?: number;
    showPageSizeSelect?: boolean;
    tableElement?: React.RefObject<HTMLElement | null>;
    variant?: RowCountLabelVariant;
}
export function Pagination({
    current,
    onChangePage,
    total: customTotal,
    pageSize,
    onPageSizeChange,
    pageLabel,
    getRowCountLabel,
    getSinglePageRowCountLabel,
    totalRowCount,
    tableElement,
    pageSizeOptions = PAGE_SIZE_OPTIONS,
    showPageSizeSelect = false,
    variant = "listView",
}: PaginationProps) {
    const pageCount =
        customTotal ??
        (totalRowCount !== undefined ? Math.ceil(totalRowCount / pageSize) : 1);
    const showPager = pageCount > 1;
    const hasRows = totalRowCount !== undefined && totalRowCount > 0;
    const hasRowCountLabel =
        hasRows &&
        (getRowCountLabel !== null ||
            (!showPager && getSinglePageRowCountLabel !== undefined));

    if (!hasRowCountLabel && !showPager && !showPageSizeSelect) {
        return null;
    }

    const handlePageChange = (page: number) => {
        if (page === current) {
            return;
        }
        onChangePage(page);
        const element = tableElement?.current;
        if (element) {
            element.scrollIntoView({ block: "start" });
        }
    };
    return (
        <PaginationNav className={paginationStyle({ variant })}>
            <RowCountLabel
                current={current}
                pageSize={pageSize}
                totalRowCount={totalRowCount}
                showPager={showPager}
                getRowCountLabel={getRowCountLabel}
                getSinglePageRowCountLabel={getSinglePageRowCountLabel}
                variant={variant}
            />
            {(showPager || showPageSizeSelect) && (
                <div className={theme.centeredContent}>
                    {showPager && (
                        <PaginationPager
                            current={current}
                            total={pageCount}
                            onChangePage={handlePageChange}
                        />
                    )}
                    {showPageSizeSelect && (
                        <div>
                            <PageSizeSelect
                                onChange={onPageSizeChange}
                                options={pageSizeOptions}
                                value={pageSize}
                                pageLabel={pageLabel}
                            />
                        </div>
                    )}
                </div>
            )}
        </PaginationNav>
    );
}

const rowCountStyle = cva(theme.rowCount, {
    variants: {
        variant: {
            listView: theme.rowCountListView,
            inline: theme.rowCountInline,
        },
    },
    defaultVariants: {
        variant: "listView",
    },
});

export type RowCountLabelVariant = NonNullable<
    VariantProps<typeof rowCountStyle>["variant"]
>;
interface RowCountLabelProps {
    current: number;
    pageSize: number;
    totalRowCount: number | undefined;
    showPager: boolean;
    getRowCountLabel:
        | ((pageSize: number, totalRowCount: number) => string)
        | null;
    getSinglePageRowCountLabel?: (totalRowCount: number) => string;
    variant: RowCountLabelVariant;
}
function RowCountLabel({
    current,
    pageSize,
    totalRowCount,
    showPager,
    getRowCountLabel,
    getSinglePageRowCountLabel,
    variant,
}: RowCountLabelProps) {
    const effectiveTotalRowCount = totalRowCount ?? 1;
    const hasRows = totalRowCount !== undefined && totalRowCount > 0;
    const useSinglePageLabel =
        hasRows && !showPager && getSinglePageRowCountLabel !== undefined;
    const useMultiPageLabel = hasRows && getRowCountLabel !== null;
    const singlePageLabel = useSinglePageLabel
        ? getSinglePageRowCountLabel(effectiveTotalRowCount)
        : null;
    const shownOnCurrentPage = Math.min(
        pageSize,
        effectiveTotalRowCount - current * pageSize
    );
    const label =
        singlePageLabel ??
        (useMultiPageLabel
            ? getRowCountLabel(shownOnCurrentPage, effectiveTotalRowCount)
            : null);

    if (label === null) {
        return null;
    }
    return <div className={rowCountStyle({ variant })}>{label}</div>;
}

interface PaginationPagerProps {
    current: number;
    total: number;
    onChangePage: (page: number) => void;
}
function PaginationPager({
    current: currentIndex,
    total,
    onChangePage,
}: PaginationPagerProps) {
    const pageNumbers = getPageNumbers(currentIndex, total);
    return (
        <PaginationContent
            className={cx(total >= FIXED_WIDTH_PAGE_COUNT && theme.fixedWidth)}
        >
            <PaginationItem>
                <PaginationPrevious
                    onClick={(e) => {
                        if (currentIndex === 0) {
                            e.preventDefault();
                            return;
                        }
                        onChangePage(Math.max(0, currentIndex - 1));
                    }}
                />
            </PaginationItem>

            {pageNumbers.map((page, index) => {
                if (page === "ellipsis") {
                    return (
                        <PaginationItem key={`ellipsis-${index}`}>
                            <PaginationEllipsis />
                        </PaginationItem>
                    );
                }
                return (
                    <PaginationItem key={page}>
                        <PaginationLink
                            active={currentIndex + 1 === page}
                            onClick={() => onChangePage(page - 1)}
                        >
                            {page}
                        </PaginationLink>
                    </PaginationItem>
                );
            })}

            <PaginationItem>
                <PaginationNext
                    onClick={(e) => {
                        if (currentIndex === total) {
                            e.preventDefault();
                            return;
                        }
                        onChangePage(Math.min(total - 1, currentIndex + 1));
                    }}
                />
            </PaginationItem>
        </PaginationContent>
    );
}

function getPageNumbers(
    currentIndex: number,
    total: number
): Array<number | "ellipsis"> {
    const pages: Array<number | "ellipsis"> = [];
    const current = currentIndex + 1;
    let startPage = Math.max(1, current - Math.floor(MAX_VISIBLE_PAGES / 2));
    const endPage = Math.min(total, startPage + MAX_VISIBLE_PAGES - 1);

    if (endPage - startPage + 1 < MAX_VISIBLE_PAGES) {
        startPage = Math.max(1, endPage - MAX_VISIBLE_PAGES + 1);
    }

    if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) {
            pages.push("ellipsis");
        }
    }

    for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
    }

    if (endPage < total) {
        if (endPage < total - 1) {
            pages.push("ellipsis");
        }
        pages.push(total);
    }

    return pages;
}
